import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box, Heading, Text, HStack, Table, Thead, Tbody, Tr, Th, Td, Badge,
  Spinner, Input, Button, Flex, Link, useColorModeValue,
} from '@chakra-ui/react';
import { supabase } from '../../supabaseClient';

const ymd = (dt) => {
  const d = new Date(dt);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};
const fmtTime = (ms) => new Date(ms).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' });
const fmtDay = (s) => new Date(`${s}T12:00:00`).toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
const fmtSpan = (ms) => {
  if (!ms || ms < 0) return '—';
  const m = Math.round(ms / 60000);
  const h = Math.floor(m / 60);
  const mm = m % 60;
  return h ? `${h}h ${mm}m` : `${mm}m`;
};

// Admin view of phleb portal opens (GPS + time) for hour tracking. First/last
// open per day approximates the span they were working; each open is a data point.
function PhlebHours() {
  const [visits, setVisits] = useState([]);
  const [names, setNames] = useState({});
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(() => { const d = new Date(); d.setDate(d.getDate() - 6); return ymd(d); });
  const [to, setTo] = useState(() => ymd(new Date()));
  const mutedColor = useColorModeValue('gray.600', 'gray.400');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const fromIso = new Date(`${from}T00:00:00`).toISOString();
      const toIso = new Date(`${to}T23:59:59`).toISOString();
      const [visitsRes, phlebsRes] = await Promise.all([
        supabase.from('phleb_portal_visits')
          .select('id, phlebotomist_id, opened_at, lat, lng')
          .gte('opened_at', fromIso).lte('opened_at', toIso)
          .order('opened_at', { ascending: false }).limit(5000),
        supabase.from('phlebotomist_profiles').select('id, full_name, company_name, email'),
      ]);
      setVisits(visitsRes.data || []);
      setNames(Object.fromEntries((phlebsRes.data || []).map((p) => [p.id, p.full_name || p.company_name || p.email || p.id])));
    } catch (err) {
      console.error('Phleb hours fetch error:', err);
    } finally {
      setLoading(false);
    }
  }, [from, to]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // One row per phleb per local day: first open, last open, span, # opens, last GPS.
  const rows = useMemo(() => {
    const groups = new Map();
    for (const v of visits) {
      const day = ymd(v.opened_at);
      const key = `${v.phlebotomist_id}|${day}`;
      if (!groups.has(key)) groups.set(key, { phlebId: v.phlebotomist_id, day, opens: [] });
      groups.get(key).opens.push(v);
    }
    const out = [...groups.values()].map((g) => {
      const times = g.opens.map((o) => new Date(o.opened_at).getTime());
      const first = Math.min(...times);
      const last = Math.max(...times);
      const latest = g.opens.reduce((a, b) => (new Date(a.opened_at) > new Date(b.opened_at) ? a : b));
      return { ...g, first, last, span: last - first, count: g.opens.length, lat: latest.lat, lng: latest.lng };
    });
    out.sort((a, b) => b.day.localeCompare(a.day) || (names[a.phlebId] || '').localeCompare(names[b.phlebId] || ''));
    return out;
  }, [visits, names]);

  return (
    <Box>
      <Heading size="lg" mb={1}>Phleb Hours</Heading>
      <Text color={mutedColor} fontSize="sm" mb={4}>
        Location + time are logged every ~2 minutes while a phleb has the portal open. First → last ping per day approximates hours worked.
      </Text>
      <HStack mb={4} spacing={2} flexWrap="wrap">
        <Text fontSize="sm" color={mutedColor}>From</Text>
        <Input type="date" size="sm" w="160px" value={from} max={to} onChange={(e) => setFrom(e.target.value)} />
        <Text fontSize="sm" color={mutedColor}>to</Text>
        <Input type="date" size="sm" w="160px" value={to} min={from} onChange={(e) => setTo(e.target.value)} />
        <Button size="sm" onClick={fetchData} isLoading={loading}>Refresh</Button>
      </HStack>

      {loading ? (
        <Flex py={10} justify="center"><Spinner size="lg" /></Flex>
      ) : rows.length === 0 ? (
        <Text color={mutedColor}>No portal opens in this range.</Text>
      ) : (
        <Box overflowX="auto" borderWidth="1px" borderColor="gray.200" borderRadius="md">
          <Table size="sm">
            <Thead bg="gray.50">
              <Tr>
                <Th>Date</Th>
                <Th>Phlebotomist</Th>
                <Th>First seen</Th>
                <Th>Last seen</Th>
                <Th>On portal</Th>
                <Th isNumeric>Pings</Th>
                <Th>Last location</Th>
              </Tr>
            </Thead>
            <Tbody>
              {rows.map((r) => (
                <Tr key={`${r.phlebId}-${r.day}`}>
                  <Td whiteSpace="nowrap">{fmtDay(r.day)}</Td>
                  <Td fontWeight="600">{names[r.phlebId] || r.phlebId}</Td>
                  <Td whiteSpace="nowrap">{fmtTime(r.first)}</Td>
                  <Td whiteSpace="nowrap">{fmtTime(r.last)}</Td>
                  <Td whiteSpace="nowrap"><Badge colorScheme="blue">{fmtSpan(r.span)}</Badge></Td>
                  <Td isNumeric>{r.count}</Td>
                  <Td>
                    {r.lat != null && r.lng != null ? (
                      <Link href={`https://www.google.com/maps/search/?api=1&query=${r.lat},${r.lng}`} isExternal color="blue.600" fontSize="xs">
                        Map
                      </Link>
                    ) : (
                      <Text fontSize="xs" color="gray.400">no GPS</Text>
                    )}
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </Table>
        </Box>
      )}
    </Box>
  );
}

export default PhlebHours;
